import { Building2, CreditCard, Home, Menu, Users, Receipt, Settings, LogOut, User as UserIcon } from 'lucide-react';
import { Link, useLocation } from '@tanstack/react-router';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

export function BottomNav() {
  const location = useLocation();
  const path = location.pathname;
  const [moreOpen, setMoreOpen] = useState(false);

  return (
    <nav
      className={cn(
        'lg:hidden fixed bottom-0 inset-x-0 z-30',
        'bg-white dark:bg-zinc-950',
        'border-t border-zinc-200 dark:border-zinc-700',
        'pb-safe',
      )}
      aria-label="Bottom navigation"
    >
      <div className="grid grid-cols-5 h-16">
        <Item to="/" icon={<Home className="w-5 h-5" />} label="Home" active={path === '/'} />
        <Item
          to="/businesses"
          icon={<Building2 className="w-5 h-5" />}
          label="Business"
          active={path.startsWith('/businesses')}
        />
        <Item
          to="/billing"
          icon={<CreditCard className="w-5 h-5" />}
          label="Billing"
          active={path.startsWith('/billing') || path.startsWith('/subscription')}
        />
        <Item
          to="/profile"
          icon={<UserIcon className="w-5 h-5" />}
          label="Profile"
          active={path.startsWith('/profile')}
        />
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          aria-haspopup="menu"
          aria-expanded={moreOpen}
          className={cn(
            'flex flex-col items-center justify-center gap-0.5 transition',
            'text-zinc-500 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-zinc-50',
          )}
        >
          <Menu className="w-5 h-5" />
          <span className="text-[11px] font-medium">More</span>
        </button>
      </div>

      {moreOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/30" onClick={() => setMoreOpen(false)} aria-hidden="true" />
          <div role="menu" className="fixed bottom-16 inset-x-0 z-50 bg-white dark:bg-zinc-950 border-t border-zinc-200 dark:border-zinc-700 rounded-t-2xl p-2 pb-safe">
            <SheetLink to="/team" icon={<Users className="w-4 h-4" />} label="Team & users" onNavigate={() => setMoreOpen(false)} />
            <SheetLink to="/subscription" icon={<Receipt className="w-4 h-4" />} label="Plan & billing" onNavigate={() => setMoreOpen(false)} />
            <SheetLink to="/profile" icon={<Settings className="w-4 h-4" />} label="Settings" onNavigate={() => setMoreOpen(false)} />
            <SheetLink to="/logout" icon={<LogOut className="w-4 h-4" />} label="Sign out" onNavigate={() => setMoreOpen(false)} />
          </div>
        </>
      )}
    </nav>
  );
}

function SheetLink({ to, icon, label, onNavigate }: { to: string; icon: ReactNode; label: string; onNavigate: () => void }) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      role="menuitem"
      className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-zinc-800 dark:text-zinc-100 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
    >
      <span className="text-zinc-500 dark:text-zinc-400">{icon}</span>
      {label}
    </Link>
  );
}

function Item({
  to,
  icon,
  label,
  active,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  active: boolean;
}) {
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex flex-col items-center justify-center gap-0.5 transition',
        active
          ? 'text-brand dark:text-brand-dm'
          : 'text-zinc-500 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-zinc-50',
      )}
    >
      {icon}
      <span className="text-[11px] font-medium">{label}</span>
    </Link>
  );
}
